// src/components/elevenlabs/PodcastPage.tsx
// Podcast generator — async job with polling.

import { useCallback, useEffect, useState } from 'react';
import { Loader2, Mic, Radio } from 'lucide-react';

import {
  ApiError, generatePodcast, listVoices, checkJob,
  type ELVoice, type ELJob,
} from '@/lib/api_elevenlabs';
import { useJobPoller } from '@/hooks/useJobPoller';
import { AudioPlayer } from '@/components/shared/AudioPlayer';
import { JobStatusBadge } from '@/components/shared/JobStatusBadge';
import { ApiKeyMissing } from '@/components/shared/ApiKeyMissing';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string }) => void;

export function PodcastPage({ addNotification }: { addNotification?: Notifier }) {
  const [voices, setVoices] = useState<ELVoice[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [apiMissing, setApiMissing] = useState(false);

  // Form
  const [mode, setMode] = useState<'conversation' | 'bulletin'>('conversation');
  const [sourceType, setSourceType] = useState<'url' | 'text'>('text');
  const [source, setSource] = useState('');
  const [voice1, setVoice1] = useState('');
  const [voice2, setVoice2] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Job
  const [jobId, setJobId] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const loadMeta = useCallback(async () => {
    setLoadingMeta(true);
    setApiMissing(false);
    try {
      const v = await listVoices();
      setVoices(v);
      if (v.length >= 1 && !voice1) setVoice1(v[0].voice_id);
      if (v.length >= 2 && !voice2) setVoice2(v[1].voice_id);
    } catch (e: any) {
      if (e instanceof ApiError && (e.status === 403 || e.status === 502)) setApiMissing(true);
      else addNotification?.({ type: 'error', title: 'Error cargando voces', message: e.message });
    } finally {
      setLoadingMeta(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadMeta(); }, [loadMeta]);

  const poller = useJobPoller<ELJob>({
    checkFn: checkJob,
    jobId,
    interval: 3000,
    onComplete: (r: ELJob) => {
      setAudioUrl(r.audio_url);
      addNotification?.({ type: 'success', title: 'Podcast generado' });
    },
    onFail: (r: ELJob) => {
      addNotification?.({ type: 'error', title: 'Error en podcast', message: r.error_message || 'Job failed' });
    },
  });

  const handleSubmit = async () => {
    if (!source.trim()) return;
    setSubmitting(true);
    setAudioUrl(null);
    setJobId(null);
    try {
      const voiceIds = mode === 'conversation' ? [voice1, voice2].filter(Boolean) : [voice1].filter(Boolean);
      const job = await generatePodcast({
        mode,
        source_type: sourceType,
        source: source.trim(),
        voice_ids: voiceIds,
      });
      setJobId(job.id);
    } catch (e: any) {
      addNotification?.({ type: 'error', title: 'Error creando podcast', message: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (apiMissing) return <ApiKeyMissing service="ElevenLabs" />;

  if (loadingMeta) {
    return <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-white/40" /></div>;
  }

  const isPolling = poller.status === 'polling';

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Mode */}
      <div>
        <label className="text-xs text-white/50 mb-1 block">Modo</label>
        <div className="flex gap-2">
          {(['conversation', 'bulletin'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-4 py-2 text-sm rounded-lg font-medium transition-all ${
                mode === m ? 'bg-[var(--acc1)] text-white shadow' : 'bg-white/5 text-white/50 hover:text-white/80'
              }`}
            >
              {m === 'conversation' ? 'Conversación (2 voces)' : 'Boletín (monólogo)'}
            </button>
          ))}
        </div>
      </div>

      {/* Source type + input */}
      <div>
        <label className="text-xs text-white/50 mb-1 block">Fuente</label>
        <div className="flex gap-2 mb-2">
          {(['text', 'url'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setSourceType(t)}
              className={`px-3 py-1.5 text-xs rounded-lg transition-all ${
                sourceType === t ? 'bg-[var(--acc2)]/30 text-[var(--acc2)]' : 'bg-white/5 text-white/50'
              }`}
            >
              {t === 'text' ? 'Texto libre' : 'URL'}
            </button>
          ))}
        </div>
        {sourceType === 'text' ? (
          <textarea
            value={source}
            onChange={(e) => setSource(e.target.value)}
            rows={5}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[var(--acc1)] resize-y"
            placeholder="Pega el contenido para el podcast…"
          />
        ) : (
          <input
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[var(--acc1)]"
            placeholder="https://ejemplo.com/articulo"
          />
        )}
      </div>

      {/* Voice pickers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <VoicePicker label="Voz 1" voices={voices} value={voice1} onChange={setVoice1} />
        {mode === 'conversation' && (
          <VoicePicker label="Voz 2" voices={voices} value={voice2} onChange={setVoice2} />
        )}
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={submitting || isPolling || !source.trim()}
        className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[var(--acc1)] text-white font-medium hover:opacity-90 disabled:opacity-40 transition-opacity"
      >
        {submitting || isPolling ? <Loader2 size={16} className="animate-spin" /> : <Radio size={16} />}
        {isPolling ? 'Generando podcast…' : 'Generar podcast'}
      </button>

      {/* Polling status */}
      {jobId && poller.status !== 'idle' && (
        <div className="o-card flex items-center gap-3">
          <JobStatusBadge status={poller.data?.status || 'pending'} />
          <span className="text-sm text-white/60">
            {poller.status === 'polling' && 'Procesando podcast…'}
            {poller.status === 'completed' && 'Podcast listo'}
            {poller.status === 'failed' && (poller.error || 'Error')}
          </span>
        </div>
      )}

      {/* Result */}
      {audioUrl && (
        <div className="o-card border border-emerald-500/30">
          <p className="text-xs text-white/50 mb-2 flex items-center gap-1.5">
            <Mic size={12} /> Podcast generado
          </p>
          <AudioPlayer src={audioUrl} />
        </div>
      )}
    </div>
  );
}

function VoicePicker({
  label, voices, value, onChange,
}: {
  label: string; voices: ELVoice[]; value: string; onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="text-xs text-white/50 mb-1 block">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--acc1)]"
      >
        {voices.map((v) => (
          <option key={v.voice_id} value={v.voice_id}>
            {v.name} ({v.category})
          </option>
        ))}
      </select>
    </div>
  );
}
